import {
  DEFAULT_AUTO_ROTATE_SPEED,
  DEFAULT_CAMERA_POSITION,
  DEFAULT_CAMERA_TARGET,
  DEFAULT_SURROUNDING_ID,
} from "Pods/ThreeDStudio/constants"
import { MATERIAL, MODEL_ID } from 'Pods/ThreeDStudio/Store/constants'

const isPoseVector = (value) => (
  value != null
  && typeof value === 'object'
  && Number.isFinite(value.x)
  && Number.isFinite(value.y)
  && Number.isFinite(value.z)
)

const APPLIERS = {
  showGrid: (v, value) => v.setGridSettings({ visible: value }),
  autoRotate: (v, value) => v.setCameraSettings({ autoRotate: value }),
  showHdri: (v, value) => v.setEnvironmentSettings({ showBackground: value }),
  strength: (v, value) => v.setEnvironmentSettings({ intensity: value }),
  rotation: (v, value) => v.setEnvironmentSettings({ rotation: value }),
  cameraPosition: (v, value) => {
    if (isPoseVector(value)) {
      v.setCameraPosition({ position: value })
    }
  },
  cameraTarget: (v, value) => {
    if (isPoseVector(value)) {
      v.setCameraPosition({ target: value })
    }
  },
}

const DEFAULTS = {
  showGrid: false,
  autoRotate: false,
  showHdri: false,
  strength: 1,
  rotation: 0,
  cameraPosition: null,
  cameraTarget: null,
}

const MATERIAL_TO_RENDER_MODE = {
  [MATERIAL.PBR]: 'pbr',
  [MATERIAL.BASIC]: 'basecolor',
  [MATERIAL.GEOMETRY_ONLY]: 'clay',
}

// Models whose output is already segmented should open in the segmentation
// render mode (colored by part) rather than a material channel.
const SEGMENTATION_MODEL_IDS = new Set([
  MODEL_ID.TENCENT_MESH_SEGMENTATION_PRO,
  MODEL_ID.TRIPO3D_MESH_SEGMENTATION_DEFAULT,
])

export const defaultRenderModeForMaterial = (material) =>
  MATERIAL_TO_RENDER_MODE[material] ?? 'pbr'

export const initialRenderModeForModel = (material, modelId) =>
  (SEGMENTATION_MODEL_IDS.has(modelId) ? 'segmentation' : defaultRenderModeForMaterial(material))

/**
 * Applies the persisted-settings table to the viewer in one shot.
 * Plain function: no React, no store reads. Called from ViewerContent after
 * the model-load promise (and the background-images query) resolves, so the
 * appliers never fire while the viewer is still mid-load (which previously
 * caused "render mode X is not available" crashes on session switch).
 *
 * `images` is the resolved background-images list, used solely to map
 * `settings.surroundingId` -> the URL that `viewer.loadEnvironment` needs.
 * Pass an empty array if the list isn't available; the surrounding apply is
 * then a no-op.
 *
 * `material` drives the render-mode + wireframe reset: every model load
 * starts with wireframe off and the channel that matches the model's
 * material (PBR/Basic/Geometry Only). These two are no longer sourced from
 * the persisted settings record.
 */
export function applyInitialSettings(viewer, settings, images, material, modelId) {
  if (!viewer) return
  const imageList = images ?? []

  // Always-on viewer flag, not part of the persisted settings field.
  viewer.setEnvironmentSettings({ enabled: true })
  viewer.setWireframeSettings({ visible: false, quadColor: 0x000000, triColor: 0x000000 })
  viewer.setCameraSettings({ autoRotateSpeed: DEFAULT_AUTO_ROTATE_SPEED })

  try {
    viewer.setRenderModeSettings({ mode: initialRenderModeForModel(material, modelId) })
  } catch {
    // Ignore error
  }

  Object.entries(APPLIERS).forEach(([key, apply]) => {
    const value = settings?.[key] ?? DEFAULTS[key]
    if (value == null) return
    apply(viewer, value)
  })

  if (!isPoseVector(settings?.cameraPosition) && !isPoseVector(settings?.cameraTarget)) {
    viewer.setCameraPosition?.({ position: DEFAULT_CAMERA_POSITION, target: DEFAULT_CAMERA_TARGET })
  }

  // Default to Studio Soft Light only if the API response confirms it exists.
  const defaultSurroundingExists = imageList.some(
    img => String(img.id) === String(DEFAULT_SURROUNDING_ID),
  )
  const surroundingId = settings?.surroundingId
    ?? (defaultSurroundingExists ? DEFAULT_SURROUNDING_ID : null)
  if (surroundingId != null) {
    const image = imageList.find(img => String(img.id) === String(surroundingId))
    if (image) {
      viewer.loadEnvironment(image.background_url).catch(() => {})
    }
  }
}

export default applyInitialSettings
